import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface SelectedAsinMarketplaceSubdate {
  dateType: string;
  dateValueInMillis?: number;
  dateStatus?: CandidateDateStatus;
}

export interface SelectedAsinMarketplaceDate {
  asin: string;
  obfuscatedMarketplaceId: string;
  dates: SelectedAsinMarketplaceSubdate[];
}

interface AsinMarketplaceBaselineCandidatesState {
  // Key is asin_obf_dateType. Ex: 123456_ATVPDKIKX0DER_announceDate
  candidateDatesMap: { [key: string]: CandidateDateWithDescriptor[] };
  selectedAnnounceDates: { [asin: string]: SelectedAsinMarketplaceDate };
  selectedOnlineStreetDates: { [asin: string]: SelectedAsinMarketplaceDate };
  programId: string | undefined;
  scheduleId: string | undefined;
  isGettingCandidates: boolean;
}

const initialState: AsinMarketplaceBaselineCandidatesState = {
  candidateDatesMap: {},
  selectedAnnounceDates: {},
  selectedOnlineStreetDates: {},
  programId: undefined,
  scheduleId: undefined,
  isGettingCandidates: false,
};

export type CandidateDateStatus = "unAssigned" | "noDatesAvailable";

export type CandidateDateType =
  | "announceDate"
  | "onlineStreetDate"
  | "offlineStreetDate";

export type CandidateDateWithDescriptor = {
  dateValueInMillis?: number;
  dateStatus?: CandidateDateStatus;
  descriptor?: string;
  marketplaceId?: string;
};

const appSlice = createSlice({
  name: "asinMarketplaceBaselineCandidatesReducer",
  initialState,
  reducers: {
    startGetCandidateDates: (state) => {
      state.isGettingCandidates = true;
    },
    endGetCandidateDates: (state) => {
      state.isGettingCandidates = false;
    },
    setCandidateDates: (
      state,
      action: PayloadAction<{ [key: string]: CandidateDateWithDescriptor[] }>
    ) => {
      state.candidateDatesMap = action.payload;
    },
    addCandidateDate: (
      state,
      action: PayloadAction<{ key: string; value: CandidateDateWithDescriptor }>
    ) => {
      const { key, value } = action.payload;
      state.candidateDatesMap[key] = [
        ...(state.candidateDatesMap[key] || []),
        value,
      ];
    },
    clearCandidateDates: (state) => {
      state.candidateDatesMap = {};
    },
    addSelectedAnnounceDate: (
      state,
      action: PayloadAction<SelectedAsinMarketplaceDate>
    ) => {
      state.selectedAnnounceDates[
        action.payload.asin + "_" + action.payload.obfuscatedMarketplaceId
      ] = action.payload;
    },
    clearSelectedAnnounceDates: (state) => {
      state.selectedAnnounceDates = {};
    },
    addSelectedOnlineStreetDate: (
      state,
      action: PayloadAction<SelectedAsinMarketplaceDate>
    ) => {
      state.selectedOnlineStreetDates[
        action.payload.asin + "_" + action.payload.obfuscatedMarketplaceId
      ] = action.payload;
    },
    clearSelectedOnlineStreetDates: (state) => {
      state.selectedOnlineStreetDates = {};
    },
    setProgramId: (state, action: PayloadAction<string>) => {
      state.programId = action.payload;
    },
    setScheduleId: (state, action: PayloadAction<string>) => {
      state.scheduleId = action.payload;
    },
  },
});

export const {
  startGetCandidateDates,
  endGetCandidateDates,
  addCandidateDate,
  setCandidateDates,
  clearCandidateDates,
  addSelectedAnnounceDate,
  clearSelectedAnnounceDates,
  addSelectedOnlineStreetDate,
  clearSelectedOnlineStreetDates,
  setProgramId,
  setScheduleId,
} = appSlice.actions;
export default appSlice.reducer;
